import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FailModal from '../components/FailModal';
import Footer from '../components/Footer';
import Header from '../components/Header';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import SearchBox from '../components/SearchBox';
import SuccessModal from '../components/SuccessModal';
import SuspendedAlertModal from '../components/SuspendedAlertModal';
import ApiDocumentationPanel from '../components/useData/ApiDocumentationPanel';
import UseTable from '../components/useData/UseTable';
import { useAuth } from '../contexts/AuthContext';
import { ApiDataItem, DataOpenItem, TableItem, TransformedGroup } from '../props/request';
import { commonUtil } from '../services/commonUtil';
import envService from '../services/envService';
import { useTranslation } from '../utils/i18n';
import { getUserRegisterId } from '../utils/userUtils';
import './UseData.css';
const API_FILE: string = envService.getApiFileUrl();


function getFileIcon(fileName: string): string {
  if (fileName.toLowerCase().endsWith('.pdf')) {
    return '📄';
  }
  if (fileName.toLowerCase().endsWith('.doc') || fileName.toLowerCase().endsWith('.docx')) {
    return '📝';
  }
  if (fileName.toLowerCase().match(/\.(jpg|jpeg|png)$/i)) {
    return '🖼️';
  }
  return '📁';
}

const UseData: React.FC = () => {
  const { t, currentLocale } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [tableData, setTableData] = useState<TransformedGroup[]>([]);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [userSuspended, setUserSuspended] = useState<boolean>(false);
  const [showSuspendedAlert, setShowSuspendedAlert] = useState<boolean>(false);

  // Sorting states
  const [sortField, setSortField] = useState<string>("DATA_OPEN_TITLE_NAME_TH");
  const [sortDirection, setSortDirection] = useState<string>("ASC");

  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showFailModal, setShowFailModal] = useState<boolean>(false);
  const [showRequestModal, setShowRequestModal] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  // New states for custom modal
  const [requestPurpose, setRequestPurpose] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileToken, setFileToken] = useState<string | null>(null);

  // Panel states
  const [showPanel, setShowPanel] = useState<boolean>(false);
  const [panelData, setPanelData] = useState({
    title: "",
    data: "",
    detail: "",
    requestDataApproveReason: "",
    requestId: ""
  });

  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    title: "",
    data: "",
    detail: ""
  });

  const columns = [
    { key: "title", label: t("requestdata.request.table.title") },
    { key: "updatedate", label: t("requestdata.request.table.updatedate") },
    { key: "version", label: t("requestdata.request.table.version") },
    { key: "status", label: t("requestdata.request.table.status") }
  ];


  const handleAction = (item: TableItem) => {
    // Check if the item status is approved
    const isApproved = item.requestStatus === "approved" ||
      item.status === t("data.status.approved") ||
      item.status === "Approved" ||
      item.status === "อนุมัติแล้ว";

    if (!isApproved) {
      // Show alert for non-approved items
      setModalMessage(t("data.messages.onlyApprovedCanView") || "สามารถดูรายละเอียดได้เฉพาะข้อมูลที่ได้รับการอนุมัติแล้วเท่านั้น");
      setShowFailModal(true);
      return;
    }

    // Find the group that contains this item to get the group label
    const group = tableData.find(g => g.items.some(i => i.id === item.id));
    const groupLabel = group ? group.label : item.title;

    // Set panel data and show panel instead of navigating
    setPanelData({
      title: groupLabel,
      data: item.data,
      detail: item.detail,
      requestDataApproveReason: item.requestDataApproveReason || '',
      requestId: item.requestDataId || item.id
    });
    setShowPanel(true);
  };

  // Panel handlers
  const handleClosePanel = () => {
    setShowPanel(false);
    setPanelData({
      title: "",
      data: "",
      detail: "",
      requestDataApproveReason: "",
      requestId: ""
    });
  };

  // Check user suspension status
  const checkUserSuspensionStatus = async () => {
    try {
      const userRegisterId = getUserRegisterId();
      const API_BASE_URL = envService.getApiBaseUrl() || 'http://localhost:8081/ods-service';
      const apiUrl = `${API_BASE_URL}/api/user-register/${userRegisterId}/detail`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('User detail response:', result);
        
        // Check if user is suspended - API returns data in nested structure
        const userData = result.data || result;
        if (userData.user_register_detail_status === 'SUSPENDED') {
          console.log('User is SUSPENDED, setting userSuspended to true');
          setUserSuspended(true);
        } else {
          console.log('User is not suspended, status:', userData.user_register_detail_status);
          setUserSuspended(false);
        }
      }
    } catch (err) {
      console.error('Error checking user suspension status:', err);
      // Don't set error state for this check, just log it
    }
  };

  // Close suspended alert
  const closeSuspendedAlert = () => {
    setShowSuspendedAlert(false);
  };

  const handleDataChange = (updatedData: TransformedGroup[]) => {
    // When working with filtered data, we need to merge changes back to the original dataset
    if (activeTab === 'all') {
      setTableData(updatedData);
    } else {
      // Merge filtered changes back to the original tableData
      setTableData(prevData => {
        return prevData.map(group => {
          const updatedGroup = updatedData.find(g => g.id === group.id);
          if (updatedGroup) {
            return {
              ...group,
              checked: updatedGroup.checked,
              items: group.items.map(item => {
                const updatedItem = updatedGroup.items.find(i => i.id === item.id);
                return updatedItem ? { ...item, checked: updatedItem.checked } : item;
              })
            };
          }
          return group;
        });
      });
    }
  };

  const requestData = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const page = currentPage - 1;

      // Map activeTab to API status parameter
      let statusParam = '';
      if (activeTab === 'active') {
        statusParam = 'ACTIVE';
      } else if (activeTab === 'requested') {
        statusParam = 'Requested';
      }
      // For 'all' tab, leave statusParam empty to get all statuses

      const userRegisterId = getUserRegisterId() || '';
      const API_BASE_URL = envService.getApiBaseUrl() || 'http://localhost:8081/ods-service';
      const apiUrl = new URL(API_BASE_URL + '/api/data-open/use-data');
      apiUrl.searchParams.set('page', page.toString());
      apiUrl.searchParams.set('size', itemsPerPage.toString());
      apiUrl.searchParams.set('sort', sortField);
      apiUrl.searchParams.set('direction', sortDirection);
      // apiUrl.searchParams.set('status', statusParam);
      apiUrl.searchParams.set('searchKeyword', searchKeyword);
      apiUrl.searchParams.set('userRegisterId', userRegisterId);
      apiUrl.searchParams.set('requestDataStatus', "2");
      apiUrl.searchParams.set('statusActive', "ACTIVE");

      console.log("API URL:", apiUrl.toString());
      console.log("Active Tab:", activeTab, "Status Param:", statusParam);

      const response = await fetch(apiUrl.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Received Response:", data);

      setApiResponse(data);
      setTotalElements(data.totalElements);

    } catch (error) {
      console.error("Error in requestData:", error);
      setApiResponse(null);
      setTotalElements(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchKeyword, activeTab, sortField, sortDirection]);

  // Handle search with debouncing
  const handleSearch = useCallback((searchTerm: string) => {
    console.log("Search Query:", searchTerm);
    setSearchKeyword(searchTerm);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    if (newPage !== currentPage && initialized && !isLoading) {
      setCurrentPage(newPage);
    }
  }, [currentPage, initialized, isLoading]);

  // Handle column sorting
  const handleSort = useCallback((columnKey: string) => {
    let newSortField = '';

    // Map column keys to API sort fields
    if (columnKey === 'title') {
      newSortField = currentLocale === "th-TH" ? "DATA_OPEN_NAME_TH" : "DATA_OPEN_NAME_EN";
    } else if (columnKey === 'status') {
      newSortField = "DATA_OPEN_STATUS";
    }

    // Toggle direction if same field, otherwise default to ASC
    const newDirection = (sortField === newSortField && sortDirection === 'ASC') ? 'DESC' : 'ASC';

    setSortField(newSortField);
    setSortDirection(newDirection);
    setCurrentPage(1); // Reset to first page when sorting
  }, [currentLocale, sortField, sortDirection]);

  // Transform API data when response changes
  useEffect(() => {
    if (!apiResponse?.content || apiResponse.content.length === 0) {
      setTableData([]);
      return;
    }

    const transformedData = apiResponse.content.map((titleItem: ApiDataItem) => {
      const groupLabel = currentLocale === "th-TH"
        ? titleItem.data_open_title_name_th
        : titleItem.data_open_title_name_en;

      const items = titleItem.data_open_list.map((dataItem: DataOpenItem) => {
        let status;
        let warningDays = null; // Define warning threshold
        if (dataItem.warning_day) {
          warningDays = dataItem.warning_day;
        }

        // Helper function to create styled status display
        if (dataItem.request_status === "approved") {
          // Check for expiration warning if approved and has remaining_days
          if (dataItem.remaining_days !== null && dataItem.remaining_days !== undefined) {
            if (dataItem.remaining_days <= 0) {
              status = "";
            } else if (warningDays && dataItem.remaining_days <= warningDays) {
              status = t("home.status.expiresIn", { days: dataItem.remaining_days });
            } else {
              status = t("shared.active");
            }
          } else {
            status = t("shared.active");
          }
        } else {
          status = t("shared.active");
        }

        return {
          id: dataItem.data_open_id,
          requestDataId: dataItem.request_data_id,
          title: groupLabel,
          data: currentLocale === "th-TH" ? dataItem.data_open_name_th : dataItem.data_open_name_en,
          data_open_name_th: dataItem.data_open_name_th,
          data_open_name_en: dataItem.data_open_name_en,
          detail: currentLocale === "th-TH" ? dataItem.data_open_detail_name_th : dataItem.data_open_detail_name_en,
          updatedate: dataItem.updated_date,
          version: dataItem.version,
          version_changed_flag: dataItem.version_changed_flag,
          status: status,
          requestStatus: dataItem.request_status,
          remainingDays: dataItem.remaining_days,
          warningDay: dataItem.warning_day,
          requestDataApproveReason: dataItem.request_data_approve_reason,
          srcStatus: dataItem.data_open_status,
          checked: false
        };
      });

      return {
        id: titleItem.data_open_title_id,
        label: groupLabel,
        checked: false,
        items: items
      };
    });

    setTableData(transformedData);
  }, [apiResponse?.content, currentLocale]);

  // Load data when page, search, or activeTab changes
  useEffect(() => {
    if (initialized) {
      const timeoutId = setTimeout(() => {
        requestData();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [currentPage, searchKeyword, activeTab, initialized, requestData]);

  // Initialize data load and check user suspension status
  useEffect(() => {
    if (currentLocale && !initialized) {
      setInitialized(true);
      checkUserSuspensionStatus();
    }
  }, [currentLocale, initialized]);

  // Check for suspension on component mount
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      checkUserSuspensionStatus();
    }
  }, [isAuthenticated, authLoading]);

  // Show suspended alert when user tries to access data
  useEffect(() => {
    if (userSuspended && initialized) {
      setShowSuspendedAlert(true);
    }
  }, [userSuspended, initialized]);

  // Since we're now doing server-side filtering, we can use tableData directly
  // No need for client-side filtering as the API returns filtered results

  // Get checked items for bulk operations
  const getCheckedItems = useCallback(() => {
    const checkedItems: TableItem[] = [];
    tableData.forEach(group => {
      group.items.forEach(item => {
        if (item.checked) {
          checkedItems.push(item);
        }
      });
    });
    return checkedItems;
  }, [tableData]);

  // Handle cancel request (call API to update status)
  const handleCancelRequest = useCallback(async () => {
    const checkedItems = getCheckedItems();

    // Check if there are any non-requested items selected
    const nonRequestedItems = checkedItems.filter(item =>
      item.status !== t("requestdata.request.tabs.requested") &&
      item.status !== 'Requested' &&
      item.status !== 'ส่งคำขอแล้ว'
    );

    if (nonRequestedItems.length > 0) {
      setModalMessage(t("requestdata.request.messages.canCancelRequestedOnly"));
      setShowFailModal(true);
      return;
    }

    if (checkedItems.length === 0) {
      setModalMessage(t("requestdata.request.messages.selectDataFirst"));
      setShowFailModal(true);
      return;
    }

    try {
      setIsCancelling(true);

      const API_BASE_URL = envService.getApiBaseUrl() || 'http://localhost:8081/ods-service';
      const apiUrl = `${API_BASE_URL}/api/request-data/bulk/status`;

      // Prepare cancel request data
      console.log("Preparing cancel request data for items:", checkedItems);

      // For cancel requests, use requestDataId if available, otherwise fall back to id
      const requestDataIds = checkedItems.map(item => {
        if (item.requestDataId) {
          return item.requestDataId;
        } else {
          console.warn(`Item ${item.id} does not have requestDataId, using data_open_id as fallback`);
          return item.id;
        }
      });

      const cancelRequestData = {
        requestDataIds: requestDataIds,
        status: 0, // 0 for cancelled status
        updatedBy: "admin"
      };

      console.log("Cancel Request Data:", cancelRequestData);

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cancelRequestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Cancel Request Response:", result);

      // Show success modal for cancel
      setSuccessMessage(t("requestdata.request.messages.cancelSuccess"));
      setShowSuccessModal(true);

      // Clear checked items and refresh data
      const updatedData = tableData.map(group => ({
        ...group,
        checked: false,
        items: group.items.map(item => ({
          ...item,
          checked: false
        }))
      }));
      setTableData(updatedData);
      requestData();

    } catch (error) {
      console.error("Error cancelling request:", error);
      setModalMessage(t("requestdata.request.messages.submitError"));
      setShowFailModal(true);
    } finally {
      setIsCancelling(false);
    }
  }, [tableData, getCheckedItems, t, requestData]);

  // --- Confirmation Modal Handler ---
  const handleConfirmModalClose = useCallback(() => {
    setShowRequestModal(false);
  }, []);

  const handleConfirmModalConfirm = useCallback(() => {
    setShowRequestModal(false);
    submitRequest();
  }, []);

  // --- Submit request with file IDs ---
  const submitRequestWithFileIds = useCallback(async (fileIds: string[]) => {
    const checkedItems = getCheckedItems();
    if (checkedItems.length === 0) {
      setModalMessage(t("requestdata.request.messages.selectDataFirst"));
      setShowFailModal(true);
      return;
    }
    // Check for inactive items - only allow active items for submission
    const inactiveItems = checkedItems.filter(item =>
      item.status === t("shared.inactive") || item.status === 'Inactive' || item.status === 'ปิดการใช้งาน'
    );
    if (inactiveItems.length > 0) {
      setModalMessage(t("requestdata.request.messages.cannotSubmitInactive"));
      setShowFailModal(true);
      return;
    }
    // Also check that we only have active items (not requested items for new submissions)
    const nonActiveItems = checkedItems.filter(item =>
      item.status !== t("shared.active") && item.status !== 'Active' && item.status !== 'ใช้งานอยู่'
    );
    if (nonActiveItems.length > 0) {
      setModalMessage(t("requestdata.request.messages.canSubmitActiveOnly"));
      setShowFailModal(true);
      return;
    }
    try {
      setIsSubmitting(true);
      const apiUrl = envService.getApiBaseUrl() + '/api/request-data/bulk';
      const userRegisterId = getUserRegisterId();
      const currentYear = new Date().getFullYear();
      const fiscalYear = (currentYear + 543).toString();

      console.log('Submitting request with uploaded files:', uploadedFiles);
      console.log('File IDs from upload:', fileIds);

      // Create file attachments from uploaded files with referenceFileId
      const fileAttachments = uploadedFiles.map((file, index) => ({
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        fileExtName: file.name.split('.').pop() || '',
        referenceFileId: fileIds[index] || null // Map file_app_id to referenceFileId
      }));

      const bulkRequestData = checkedItems.map((item) => ({
        userRegisterId: userRegisterId,
        dataOpenId: item.id,
        requestDataObjective: requestPurpose || `Data analysis for research purposes - ${item.data}`,
        fiscalYear: fiscalYear,
        createdBy: "admin",
        updatedBy: "admin",
        fileAttachments: fileAttachments.length > 0 ? fileAttachments : undefined
      }));

      console.log('Bulk request data:', bulkRequestData);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bulkRequestData)
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      console.log('Submit request response:', result);

      setSuccessMessage(t("requestdata.request.messages.submitSuccess", { count: checkedItems.length }));
      setShowSuccessModal(true);
      // Clear checked items and refresh data
      const updatedData = tableData.map(group => ({
        ...group,
        checked: false,
        items: group.items.map(item => ({
          ...item,
          checked: false
        }))
      }));
      setTableData(updatedData);
      setShowRequestModal(false);
      requestData();
    } catch (error) {
      console.error('Error submitting request:', error);
      setModalMessage(t("requestdata.request.messages.submitError"));
      setShowFailModal(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [getCheckedItems, requestData, t, tableData, requestPurpose, uploadedFiles]);

  // --- Extracted submit logic ---
  const submitRequest = useCallback(async () => {
    const checkedItems = getCheckedItems();
    if (checkedItems.length === 0) {
      setModalMessage(t("requestdata.request.messages.selectDataFirst"));
      setShowFailModal(true);
      return;
    }
    // Check for inactive items - only allow active items for submission
    const inactiveItems = checkedItems.filter(item =>
      item.status === t("shared.inactive") || item.status === 'Inactive' || item.status === 'ปิดการใช้งาน'
    );
    if (inactiveItems.length > 0) {
      setModalMessage(t("requestdata.request.messages.cannotSubmitInactive"));
      setShowFailModal(true);
      return;
    }
    // Also check that we only have active items (not requested items for new submissions)
    const nonActiveItems = checkedItems.filter(item =>
      item.status !== t("shared.active") && item.status !== 'Active' && item.status !== 'ใช้งานอยู่'
    );
    if (nonActiveItems.length > 0) {
      setModalMessage(t("requestdata.request.messages.canSubmitActiveOnly"));
      setShowFailModal(true);
      return;
    }
    try {
      setIsSubmitting(true);
      const API_BASE_URL = envService.getApiBaseUrl() || 'http://localhost:8081/ods-service';
      const apiUrl = `${API_BASE_URL}/api/request-data/bulk`;
      const userRegisterId = getUserRegisterId();
      const currentYear = new Date().getFullYear();
      const fiscalYear = (currentYear + 543).toString();
      const bulkRequestData = checkedItems.map((item) => ({
        userRegisterId: userRegisterId,
        dataOpenId: item.id,
        requestDataObjective: requestPurpose || `Data analysis for research purposes - ${item.data}`,
        fiscalYear: fiscalYear,
        createdBy: "admin",
        updatedBy: "admin",
        attachedFileIds: uploadedFileIds.length > 0 ? uploadedFileIds : undefined
      }));
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bulkRequestData)
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setSuccessMessage(t("requestdata.request.messages.submitSuccess", { count: checkedItems.length }));
      setShowSuccessModal(true);
      // Clear checked items and refresh data
      const updatedData = tableData.map(group => ({
        ...group,
        checked: false,
        items: group.items.map(item => ({
          ...item,
          checked: false
        }))
      }));
      setTableData(updatedData);
      requestData();
    } catch (error) {
      setModalMessage(t("requestdata.request.messages.submitError"));
      setShowFailModal(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [getCheckedItems, requestData, t, tableData]);

  // --- Custom Request Modal Handlers ---
  const openRequestModal = () => {
    setShowRequestModal(true);
  };
  const closeRequestModal = () => {
    setShowRequestModal(false);
    setRequestPurpose('');
    setUploadedFiles([]);
    setUploadedFileIds([]);
  };

  // Get upload token from NEB API
  const getUploadToken = async (): Promise<string | null> => {
    try {
      const response = await fetch(envService.getMicroflowRestUrl(), {
        method: 'POST',
        headers: {
          'Accept': '*/*',
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
        },
        body: JSON.stringify({
          appName: "NEB core",
          snapVersion: "v.1.0.0",
          uniqueId: "C60467346-4ee7fd7c-f550-47e6-a1d3-26e5a18db3de",
          flowName: "getTokenUploadFile",
          object: {
            obj_getTokenUploadFile_in: {
              uuid: commonUtil.genUUID(),
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data?.responseObjectsMap?.obj_getTokenUploadFile_out?.tokenUploadFile || null;
    } catch (error) {
      console.error('Error getting upload token:', error);
      return null;
    }
  };

  // Upload file to NEB API
  const uploadFileToNEB = async (file: File, token: string): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append('fileName', file.name);
      formData.append('fileType', file.type);
      formData.append('groupId', '');
      formData.append('file', file);
      formData.append('checkLimit', "N");
      formData.append('parentFolderId', process.env.REACT_APP_PARENT_FOLDER_ID || '84c9908e-e6e6-4721-af37-4cf2a8651ac7');

      const response = await fetch(API_FILE + '/upload', {
        method: 'POST',
        headers: {
          'Accept': '*/*',
          'Authorization': `Bearer ${token}`,
          'Origin': window.location.origin,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        return data.data.file_app_id;
      }
      return null;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };

  useEffect(() => {
    const getTokenFile = async () => {
      const token = await getUploadToken();
      console.log('Upload Token:', token);
      if (!token) {
        setModalMessage(t("requestdata.request.messages.tokenError"));
        setShowFailModal(true);

      }
      setFileToken(token)
    }
    getTokenFile()
  }, [])


  const handlePurposeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setRequestPurpose(e.target.value);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadedFiles([...uploadedFiles, ...Array.from(e.target.files)]);
    }
  };
  const handleFileDelete = (index: number) => {
    setUploadedFiles(files => files.filter((_, i) => i !== index));
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      setUploadedFiles([...uploadedFiles, ...Array.from(e.dataTransfer.files)]);
    }
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };
  // --- Modified handleSubmitRequest ---
  const handleSubmitRequest = useCallback(() => {
    openRequestModal();
  }, []);
  // --- Send Request from Modal ---
  const handleSendRequest = async () => {
    // Validate purpose
    if (!requestPurpose.trim()) {
      setModalMessage(t("requestdata.request.messages.purposeRequired"));
      setShowFailModal(true);
      return;
    }

    try {
      setIsUploading(true);

      // Upload files first if any and wait for completion
      let fileIds: string[] = [];
      if (uploadedFiles.length > 0) {
        const token: any = fileToken;
        if (!token) {
          setModalMessage(t("requestdata.request.messages.tokenError"));
          setShowFailModal(true);
          return;
        }

        // Upload each file and collect IDs
        for (const file of uploadedFiles) {
          console.log('Uploading file:', file.name);
          const fileId = await uploadFileToNEB(file, token);
          if (fileId) {
            fileIds.push(fileId);
          }
        }

        console.log('All files uploaded, IDs:', fileIds);
        setUploadedFileIds(fileIds);
      }



      // Submit request with file IDs
      await submitRequestWithFileIds(fileIds);

    } catch (error) {
      console.error('Error in file upload and request process:', error);
      setModalMessage(t("requestdata.request.messages.uploadAndSubmitError"));
      setShowFailModal(true);
      setShowRequestModal(false);
    } finally {
      setIsUploading(false);
    }
  };

  // Check if any items are selected
  const hasCheckedItems = useMemo(() => {
    return tableData.some(group =>
      group.items.some(item => item.checked)
    );
  }, [tableData]);

  // Check if cancel request button should be enabled (only for requested items)
  const canCancelRequest = useMemo(() => {
    const checkedItems = getCheckedItems();
    if (checkedItems.length === 0) return false;

    // Only allow cancel if ALL selected items are "requested" status
    return checkedItems.every(item =>
      item.status === t("requestdata.request.tabs.requested") ||
      item.status === 'Requested' ||
      item.status === 'ส่งคำขอแล้ว'
    );
  }, [tableData, getCheckedItems, t]);

  // Check if submit request button should be enabled (active items or expired items)
  const canSubmitRequest = useMemo(() => {
    const checkedItems = getCheckedItems();
    if (checkedItems.length === 0) return false;

    // Allow submit for active items, rejected items, or expired items (remaining_days <= 0)
    return checkedItems.every(item => {
      // Check if item is active
      const isActive = item.status === t("shared.active") ||
        item.status === 'Active' ||
        item.status === 'ใช้งานอยู่';

      // Check if item is rejected
      const isRejected = item.requestStatus === 'rejected';

      // Check if item is expired (contains expiration text)
      const isExpired = item.status && (
        item.status.includes(t("home.status.expiresIn", { days: 0 }).split(' ')[0]) ||
        item.status.includes("หมดเวลา") ||
        item.status.includes("Expires") ||
        item.status.includes("Expira")
      );

      return isActive || isRejected || isExpired;
    });
  }, [tableData, getCheckedItems, t]);

  // Modal handlers
  const handleSuccessModalClose = useCallback(() => {
    setShowSuccessModal(false);
    setSuccessMessage('');
  }, []);

  const handleFailModalClose = useCallback(() => {
    setShowFailModal(false);
    setModalMessage('');
  }, []);

  // Authentication check - redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }
  }, [isAuthenticated, authLoading, navigate]);

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = totalElements ? Math.min(totalElements, currentPage * itemsPerPage) : currentPage * itemsPerPage;

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <LoadingSpinner size="xlarge" />
        <p>{t("shared.loading")}...</p>
      </div>
    );
  }

  // Don't render anything if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div>
      <div className="relative overflow-hidden bg-white dark:bg-gray-900 text-coolGray-800 dark:text-slate-200">
        <Header />
        <div className="container-background">
          {!showPanel ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Header with Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="text-neutral-90 fw-semibold" style={{ fontSize: '18px' }}>
                  {t("useData.title")}
                </div>
              </div>

              <SearchBox onSubmit={handleSearch} />

              {isLoading ? (
                <div className="loading-container">
                  <LoadingSpinner size="xlarge" />
                  <p className="loading-text">{t("shared.loading")}...</p>
                </div>
              ) : (
                <UseTable
                  columns={columns}
                  datas={tableData}
                  // actionId="#requestModal"
                  onHandleAction={handleAction}
                  onDataChange={handleDataChange}
                  onSort={handleSort}
                  sortField={sortField}
                  sortDirection={sortDirection}
                />
              )}

              <Pagination
                totalItems={totalElements}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : (
            <ApiDocumentationPanel
              isOpen={showPanel}
              onClose={handleClosePanel}
              data={panelData}
            />
          )}
        </div>
        <Footer />
      </div>

      {/* Request Modal */}

      <div id="requestModal" className="modal request-modal" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="text-neutral-90 fw-semibold" style={{ fontSize: '18px' }}>
                {t("requestdata.request.title")}
              </div>
            </div>
            <div className="modal-body-request modal-body">
              <div className="grid-1-column">
                <label
                  className="text-neutral-90 text-start fw-medium"
                  style={{ fontSize: '14px' }}
                >
                  {t("home.modal.title")}
                  <span className="text-danger fw-medium ml-1" style={{ fontSize: '14px' }}>
                    *
                  </span>
                  <input
                    id="title"
                    type="text"
                    className="form-control"
                    value={formData.title}
                    disabled
                    readOnly
                  />
                </label>
                <label
                  className="text-neutral-90 text-start fw-medium"
                  style={{ fontSize: '14px' }}
                >
                  {t("home.modal.data")}
                  <span className="text-danger fw-medium ml-1" style={{ fontSize: '14px' }}>
                    *
                  </span>
                  <input
                    id="data"
                    type="text"
                    className="form-control"
                    value={formData.data}
                    disabled
                    readOnly
                  />
                </label>
                <label
                  className="text-neutral-90 text-start fw-medium"
                  style={{ fontSize: '14px' }}
                >
                  {t("home.modal.detail")}
                  <span className="text-danger fw-medium ml-1" style={{ fontSize: '14px' }}>
                    *
                  </span>
                  <textarea
                    id="detail"
                    className="form-control"
                    rows={4}
                    cols={50}
                    value={formData.detail}
                    disabled
                    readOnly
                  />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-neutral-secondary btn-home-close"
                style={{ maxWidth: '130px', height: '48px' }}
                data-bs-dismiss="modal"
              >
                {t("app.shared.buttons.close")}
              </button>
            </div>
          </div>
        </div>
      </div>


      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        onConfirm={handleSuccessModalClose}
        message={successMessage}
      />

      {/* Fail Modal */}
      <div style={{ zIndex: 10000, position: 'relative' }}>
        <FailModal
          isOpen={showFailModal}
          onClose={handleFailModalClose}
          onConfirm={handleFailModalClose}
          message={modalMessage}
        />
      </div>



      {/* Custom Request Modal */}
      {showRequestModal && (
        <div className="modal-backdrop" style={{ zIndex: 1050 }}>
          <div className="modal-container" style={{ maxWidth: 700, margin: '40px auto' }}>
            <div className="modal-content" style={{ padding: 0 }}>
              <div className="modal-header" style={{ padding: '24px 24px 0 24px', borderBottom: 'none' }}>
                <div className="text-neutral-90 fw-semibold" style={{ fontSize: '18px' }}>
                  {t("requestdata.request.modal.title")}
                </div>
              </div>
              <div className="modal-body" style={{ padding: 24, width: "90%" }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, marginBottom: 8, display: 'block' }}>{t("requestdata.request.modal.purposeLabel")} <span style={{ color: 'red' }}>*</span></label>
                  <textarea
                    value={requestPurpose}
                    onChange={handlePurposeChange}
                    rows={4}
                    style={{ width: '100%', borderRadius: 8, border: '1px solid #ccc', padding: 8 }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, marginBottom: 8, display: 'block' }}>{t("requestdata.request.modal.uploadLabel")}</label>
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    style={{ border: '1px dashed #ccc', borderRadius: 8, padding: 16, textAlign: 'center', background: '#fafafa', cursor: 'pointer' }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t("requestdata.request.modal.dragDropText")} <span style={{ color: '#007bff', textDecoration: 'underline' }}>{t("requestdata.request.modal.chooseFile")}</span>
                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    />
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    {t("requestdata.request.modal.uploadHint")}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    {uploadedFiles.map((file, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 4, background: '#f5f5f5', borderRadius: 4, padding: '4px 8px' }}>
                        <span style={{ marginRight: 8 }}>{getFileIcon(file.name)}</span>
                        <span style={{ flex: 1, fontSize: 14 }}>{file.name}</span>
                        <button
                          onClick={() => handleFileDelete(idx)}
                          style={{
                            background: '#e74c3c',
                            border: 'none',
                            color: 'white',
                            fontSize: 12,
                            cursor: 'pointer',
                            borderRadius: 4,
                            padding: '4px 8px',
                            minWidth: '60px',
                            height: '24px'
                          }}
                        >
                          {t("requestdata.request.messages.deleteFile")}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ padding: 24, display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: 'none' }}>
                <button
                  type="button"
                  className="btn-modal-close btn btn-neutral-secondary btn-home-close"
                  style={{ maxWidth: '130px', height: '48px' }}
                  onClick={closeRequestModal}
                >
                  {t("requestdata.request.modal.closeButton")}
                </button>
                <button
                  type="button"
                  className="btn-modal-submit btn btn-primary"
                  style={{ maxWidth: '130px', height: '48px' }}
                  onClick={handleSendRequest}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <LoadingSpinner size="small" />
                      <span style={{ marginLeft: '8px' }}>{t("requestdata.request.modal.uploadingText")}</span>
                    </>
                  ) : (
                    t("requestdata.request.modal.submitButton")
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suspended Alert Modal */}
      <SuspendedAlertModal
        isOpen={showSuspendedAlert}
        onClose={closeSuspendedAlert}
      />
    </div>
  );
};

export default UseData;
